import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import axios from 'axios';
import { LanguageProvider } from './contexts/LanguageContext';
import { CartProvider } from './contexts/CartContext';
import { ToastProvider } from './contexts/ToastContext';
import './App.css';

// Import components (these are small, keep eager)
import Header from './components/Header';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import CartDrawer from './components/CartDrawer';
import CookieConsentBanner from './components/CookieConsentBanner';
import { clearAdminSession, isAdminTokenValid } from './utils/adminAuth';

// Lazy load pages
const Home = lazy(() => import('./pages/Home'));
const Shop = lazy(() => import('./pages/Shop'));
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogDetail = lazy(() => import('./pages/BlogDetail'));
const Contact = lazy(() => import('./pages/Contact'));
const Checkout = lazy(() => import('./pages/Checkout'));
const PaymentPage = lazy(() => import('./pages/PaymentPage'));
const Success = lazy(() => import('./pages/Success'));
const TermsOfBusiness = lazy(() => import('./pages/TermsOfBusiness'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const RightToTerminate = lazy(() => import('./pages/RightToTerminate'));

// Admin pages
const AdminLogin = lazy(() => import('./components/AdminLogin'));
const AdminLayout = lazy(() => import('./pages/admin/AdminLayout'));
const Dashboard = lazy(() => import('./pages/admin/Dashboard'));
const AdminBlogs = lazy(() => import('./pages/admin/Blogs'));
const AdminProducts = lazy(() => import('./pages/admin/Products'));
const AdminDiscountCodes = lazy(() => import('./pages/admin/DiscountCodes'));
const AdminOrders = lazy(() => import('./pages/admin/Orders'));
const AdminCategories = lazy(() => import('./pages/admin/Categories'));
const AdminShippingRates = lazy(() => import('./pages/admin/ShippingRates'));
const AdminProductTags = lazy(() => import('./pages/admin/ProductTags'));
const AdminDealerships = lazy(() => import('./pages/admin/Dealerships'));

// Loading fallback component
const LoadingFallback = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '60vh',
    fontSize: '1.2rem',
    color: '#C77735'
  }}>
    <div>Loading...</div>
  </div>
);

// Protected route wrapper
const ProtectedAdminRoute = ({ children }) => {
  const token = localStorage.getItem('adminToken');

  if (!token) {
    clearAdminSession();
    return <AdminLogin />;
  }

  if (!isAdminTokenValid(token)) {
    clearAdminSession();
    return <AdminLogin reason="session-expired" />;
  }

  return children;
};

function App() {
  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const statusCode = error?.response?.status;
        const requestUrl = String(error?.config?.url || '');
        const authHeader = error?.config?.headers?.Authorization || error?.config?.headers?.authorization;
        const hasBearerToken = typeof authHeader === 'string' && authHeader.toLowerCase().startsWith('bearer ');
        const isAdminLoginRequest = requestUrl.includes('/api/auth/admin-login');

        if (statusCode === 401 && hasBearerToken && !isAdminLoginRequest) {
          clearAdminSession();
          if (window.location.pathname.startsWith('/admin')) {
            window.location.href = '/admin?reason=session-expired';
          }
        }

        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptorId);
    };
  }, []);

  return (
    <LanguageProvider>
      <CartProvider>
        <ToastProvider>
          <Router>
            <ScrollToTop />
            <div className="App">
            <Routes>
            {/* Public routes with Header/Footer */}
            <Route path="/" element={
              <>
                <Header />
                <main id="main-content" tabIndex="-1" className="pt-14 sm:pt-20">
                  <Suspense fallback={<LoadingFallback />}>
                    <Home />
                  </Suspense>
                </main>
                <Footer />
              </>
            } />
            <Route path="/blog" element={
              <>
                <Header />
                <main id="main-content" tabIndex="-1" className="pt-14 sm:pt-20">
                  <Suspense fallback={<LoadingFallback />}>
                    <Blog />
                  </Suspense>
                </main>
                <Footer />
              </>
            } />
            <Route path="/blog/:id" element={
              <>
                <Header />
                <main id="main-content" tabIndex="-1" className="pt-14 sm:pt-20">
                  <Suspense fallback={<LoadingFallback />}>
                    <BlogDetail />
                  </Suspense>
                </main>
                <Footer />
              </>
            } />
            <Route path="/shop" element={
              <>
                <Header />
                <main id="main-content" tabIndex="-1" className="pt-14 sm:pt-20">
                  <Suspense fallback={<LoadingFallback />}>
                    <Shop />
                  </Suspense>
                </main>
                <Footer />
              </>
            } />
            <Route path="/product/:id" element={
              <>
                <Header />
                <main id="main-content" tabIndex="-1" className="pt-14 sm:pt-20">
                  <Suspense fallback={<LoadingFallback />}>
                    <ProductDetail />
                  </Suspense>
                </main>
                <Footer />
              </>
            } />
            <Route path="/contact" element={
              <>
                <Header />
                <main id="main-content" tabIndex="-1" className="pt-14 sm:pt-20">
                  <Suspense fallback={<LoadingFallback />}>
                    <Contact />
                  </Suspense>
                </main>
                <Footer />
              </>
            } />
            <Route path="/checkout" element={
              <>
                <Header />
                <main id="main-content" tabIndex="-1" className="pt-14 sm:pt-20">
                  <Suspense fallback={<LoadingFallback />}>
                    <Checkout />
                  </Suspense>
                </main>
                <Footer />
              </>
            } />
            <Route path="/payment" element={
              <>
                <Header />
                <main id="main-content" tabIndex="-1" className="pt-14 sm:pt-20">
                  <Suspense fallback={<LoadingFallback />}>
                    <PaymentPage />
                  </Suspense>
                </main>
                <Footer />
              </>
            } />
            <Route path="/success" element={
              <>
                <Header />
                <main id="main-content" tabIndex="-1" className="pt-14 sm:pt-20">
                  <Suspense fallback={<LoadingFallback />}>
                    <Success />
                  </Suspense>
                </main>
                <Footer />
              </>
            } />
            <Route path="/terms-of-business" element={
              <>
                <Header />
                <main id="main-content" tabIndex="-1" className="pt-14 sm:pt-20">
                  <Suspense fallback={<LoadingFallback />}>
                    <TermsOfBusiness />
                  </Suspense>
                </main>
                <Footer />
              </>
            } />
            <Route path="/privacy-policy" element={
              <>
                <Header />
                <main id="main-content" tabIndex="-1" className="pt-14 sm:pt-20">
                  <Suspense fallback={<LoadingFallback />}>
                    <PrivacyPolicy />
                  </Suspense>
                </main>
                <Footer />
              </>
            } />
            <Route path="/right-to-withdrawal" element={
              <>
                <Header />
                <main id="main-content" tabIndex="-1" className="pt-14 sm:pt-20">
                  <Suspense fallback={<LoadingFallback />}>
                    <RightToTerminate />
                  </Suspense>
                </main>
                <Footer />
              </>
            } />

            {/* Admin routes (no Header/Footer) */}
            <Route path="/admin" element={
              <Suspense fallback={<LoadingFallback />}>
                <ProtectedAdminRoute>
                  <AdminLayout />
                </ProtectedAdminRoute>
              </Suspense>
            }>
              <Route index element={<Dashboard />} />
              <Route path="blogs" element={<AdminBlogs />} />
              <Route path="products" element={<AdminProducts />} />
              <Route path="discount-codes" element={<AdminDiscountCodes />} />
              <Route path="orders" element={<AdminOrders />} />
              <Route path="categories" element={<AdminCategories />} />
              <Route path="product-tags" element={<AdminProductTags />} />
              <Route path="shipping-rates" element={<AdminShippingRates />} />
              <Route path="dealerships" element={<AdminDealerships />} />
            </Route>
            </Routes>
            <CartDrawer />
            <CookieConsentBanner />
          </div>
        </Router>
      </ToastProvider>
      </CartProvider>
    </LanguageProvider>
  );
}

export default App;
